import axios from "axios";
import { toast } from "sonner";
import { resolveApiBaseUrl } from "@/utils/apiBaseUrl";

let token = null;
let logoutFn = null;
let hasShownTokenError = false;
let activeRoute = null;

export const webTokenProvider = {
  getAccessToken: () => token || localStorage.getItem("accessToken"),
};

export const setActiveRoute = (routeName) => {
  activeRoute = routeName;
};

export const setLogoutFunction = (logout) => {
  logoutFn = logout;
};

export const setAuthToken = (accessToken) => {
  token = accessToken;
  hasShownTokenError = false;
};

export const getApiErrorMessage = (error, fallback = "An unexpected error occurred.") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;

  if (error.response?.data) {
    const data = error.response.data;
    if (typeof data === "string" && !data.startsWith("<")) return data;
    if (typeof data === "object") {
      if (data.errors && typeof data.errors === "object") {
        const fieldErrors = Object.values(data.errors).flat().filter(Boolean);
        if (fieldErrors.length > 0) return fieldErrors.join("\n");
      }
      if (data.detail) return data.detail;
      if (data.message) return data.message;
      if (data.Message) return data.Message;
      if (data.title && !["Bad Request", "Internal Server Error"].includes(data.title)) {
        return data.title;
      }
    }
  }

  if (error.code === "ECONNABORTED" || error.message?.toLowerCase().includes("timeout")) {
    return "Connection timed out. Please check your internet connection.";
  }

  if (error.message?.toLowerCase().includes("network error") || !error.response) {
    return "Unable to connect to the server. Please check your network connection.";
  }

  return error.message || fallback;
};

const axiosInstance = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Content-Type": "application/json",
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.baseURL = resolveApiBaseUrl();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (activeRoute) {
      config.headers["Active-Route"] = activeRoute;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      const message = getApiErrorMessage(error, "Session expired. Please login again.");
      if (!hasShownTokenError) {
        toast.error(message);
        hasShownTokenError = true;
      }

      if (logoutFn) {
        logoutFn();
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
